import React, { useState } from 'react';
import { Box, Text, HStack, VStack, Link, Checkbox, Spinner, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter, Textarea, Button } from '@chakra-ui/react';
import { ExternalLinkIcon, EmailIcon, PhoneIcon, CheckCircleIcon, QuestionIcon } from '@chakra-ui/icons';
import CustomFieldViewModal from './CustomFieldViewModal';
import CustomFieldDisplay from './CustomFieldDisplay';
import ComplianceStatusChip from './ComplianceStatusChip';
import { 
  getCompanyColumns, 
  getContactColumns, 
  getCompanyTableMinWidth, 
  getContactTableMinWidth,
  getColumnStyle,
  getMessageColumns,
  shouldShowSubjectColumn,
  resolveTemplateVariables,
  formatCurrency,
  formatEmployees,
  ROW_TYPES 
} from '../../../utils/tableUtils';
import { GmailLogo, OutlookLogo } from '../chat/EmailProviderLogos';

/**
 * GillyReach column — Gmail and Outlook SVGs use different viewBox padding; equal `size` makes Outlook look tiny.
 * Tune per provider so visual weight matches in the row (see EmailProviderLogos viewBoxes).
 */
const GILLYREACH_LOGO_PX = { gmail: 15, outlook: 17};

/** Same typography as empty values in CustomFieldDisplay (—, muted). */
const EmptyCell = () => (
  <Text fontSize="sm" color="rgba(255,255,255,0.2)">
    —
  </Text>
);

// Custom LinkedIn Icon Component
const LinkedInIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    width="1em"
    height="1em"
    {...props}
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

/**
 * Format phone number to (xxx) xxx-xxxx format
 * @param {string} phoneNumber - Raw phone number string
 * @returns {string} - Formatted phone number
 */
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;
  
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Handle different lengths
  if (cleaned.length === 10) {
    // Format as (xxx) xxx-xxxx
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    // Handle numbers with country code 1
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length > 10) {
    // For international numbers, just format the last 10 digits
    const lastTen = cleaned.slice(-10);
    return `(${lastTen.slice(0, 3)}) ${lastTen.slice(3, 6)}-${lastTen.slice(6)}`;
  }
  
  // Return original if it doesn't match expected patterns
  return phoneNumber;
};

/**
 * Virtualized table row component
 * Renders company rows, contact rows, or empty state rows
 * Compatible with react-window for virtualization
 * 
 * @param {Object} props
 * @param {number} props.index - Row index
 * @param {Object} props.style - Style object from react-window
 * @param {Object} props.data - Data object containing all row data and callbacks
 */
const TableRow = React.memo(({ index, style, data }) => {
  const { 
    flattenedData, 
    selectedRows, 
    onRowSelect, 
    companyFieldDefinitions, 
    contactFieldDefinitions, 
    previewMembershipIds, 
    editingCell, 
    setEditingCell, 
    handleCellEdit,
    // Use pre-calculated columns and widths from parent for consistency
    companyColumns,
    contactColumns,
    companyTableMinWidth,
    contactTableMinWidth,
    // Company detail view callback
    onCompanyClick,
    // Contact detail view callback
    onContactClick,
    // Tracking field IDs (sourced from contactFieldDefinitions with source=tracking)
    contactedFieldId,
    notesFieldId,
    // Callbacks for updating tracking fields
    onContactedChange,
    onNotesChange,
    // Message drafts for subject/body columns
    messageDrafts = [],
    // Export tracking for per-row export badges
    exportTracking = [],
    exportedGroupIdsSmartlead = new Set(),
    exportedGroupIdsHeyreach = new Set(),
    exportedGroupIdsGillyreach = new Set(),
    gillyreachContactStatus,
    collapsedExportGroupIds = new Set(),
    onMessageDraftCellSave,
    isGeneratingCustomFields = false,
    // Phase 3.5 advisor compliance — all three are stable no-ops when
    // the campaign has no accepted reviewers, so callers don't have to
    // conditionally wire them.
    advisorComplianceEnabled = false,
    getAdvisorComplianceDecision = null,
    onAdvisorComplianceChipClick = null,
    isLocked = false,
    // elasticsearch_ids whose address fetch is currently in flight (bulk action).
    addressFetchingIds = new Set(),
  } = data;
  
  const rowData = flattenedData[index];
  
  // Modal state for viewing custom field content
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const [modalFieldName, setModalFieldName] = useState('');
  const [modalFieldValue, setModalFieldValue] = useState(null);
  const [modalFieldDef, setModalFieldDef] = useState(null);
  // Modal state for notes editing
  const { isOpen: isNotesModalOpen, onOpen: onNotesModalOpen, onClose: onNotesModalClose } = useDisclosure();
  const [notesEditValue, setNotesEditValue] = useState('');
  const [notesMembershipId, setNotesMembershipId] = useState(null);
  const [notesSaving, setNotesSaving] = useState(false);
  // Modal state for message draft cells (Subject/Body) - editable
  const { isOpen: isMessageCellModalOpen, onOpen: onMessageCellModalOpen, onClose: onMessageCellModalClose } = useDisclosure();
  const [messageCellModalColName, setMessageCellModalColName] = useState('');
  const [messageCellModalValue, setMessageCellModalValue] = useState('');
  const [messageCellEditValue, setMessageCellEditValue] = useState('');
  const [messageCellFieldId, setMessageCellFieldId] = useState(null);
  const [messageCellMembershipId, setMessageCellMembershipId] = useState(null);
  const [messageCellSaving, setMessageCellSaving] = useState(false);
  
  // Use columns passed from parent to ensure consistency with headers
  const COMPANY_COLUMNS = companyColumns || getCompanyColumns(companyFieldDefinitions?.length || 0);
  const COMPANY_TABLE_MIN_WIDTH = companyTableMinWidth || getCompanyTableMinWidth(companyFieldDefinitions?.length || 0);
  
  const CONTACT_COLUMNS = contactColumns || getContactColumns(contactFieldDefinitions?.length || 0);
  const CONTACT_TABLE_MIN_WIDTH = contactTableMinWidth || getContactTableMinWidth(contactFieldDefinitions?.length || 0);
  
  // Get entity ID for selection
  const getEntityId = () => {
    if (!rowData?.data) return null;
    return rowData.data.id;
  };

  if (!rowData) return null;

  const entityId = getEntityId();
  const isSelected = selectedRows && selectedRows.has(entityId);
  
  // Check if this row has preview data
  const hasPreviewData = rowData.data?.membership_id && previewMembershipIds && previewMembershipIds.has(rowData.data.membership_id);

  switch (rowData.type) {
    case ROW_TYPES.COMPANY:
      return (
        <>
          <CustomFieldViewModal
            isOpen={isModalOpen}
            onClose={onModalClose}
            fieldName={modalFieldName}
            fieldValue={modalFieldValue}
            fieldDef={modalFieldDef}
            onSave={isLocked ? undefined : async (newValue) => {
              if (editingCell && handleCellEdit) {
                // Optimistically update the modal display
                setModalFieldValue(newValue);
                // Save to backend
                await handleCellEdit(editingCell.entityId, editingCell.fieldId, newValue, editingCell.entityType);
              }
            }}
          />
          <div style={{...style, height: `${style.height}px`, padding: 0, margin: 0, minWidth: `${COMPANY_TABLE_MIN_WIDTH}px`, width: '100%'}}>
          <Box
            bg={isSelected ? "rgba(255,255,255,0.06)" : "#0a0a0a"}
            borderLeft="1px solid"
            borderRight="1px solid"
            borderBottom="1px solid"
            borderColor="rgba(255,255,255,0.08)"
            mx={0}
            my={0}
            h="100%"
            w="100%"
            _hover={{ bg: isSelected ? "rgba(255,255,255,0.09)" : "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.12)" }}
            position="relative"
            transition="all 0.15s ease"
          >
            <HStack spacing={0} h="100%" align="stretch" w="100%">
              {/* Checkbox Column */}
              <Box {...getColumnStyle(COMPANY_COLUMNS.checkbox)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" justifyContent="center">
                <Checkbox
                  isChecked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onRowSelect && onRowSelect(entityId, index, e.nativeEvent);
                  }}
                  colorScheme="blue"
                />
              </Box>

              {/* Company Name Column */}
              <Box {...getColumnStyle(COMPANY_COLUMNS.name)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center">
                <Text
                  fontWeight="500"
                  color="#fafafa"
                  fontSize="sm"
                  noOfLines={2}
                  lineHeight="1.4"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  cursor="pointer"
                  _hover={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}
                  transition="color 0.12s ease"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCompanyClick && onCompanyClick(rowData.data);
                  }}
                >
                  {rowData.data.company_name || 'Unknown Company'}
                </Text>
              </Box>

              {/* Headcount Column */}
              <Box {...getColumnStyle(COMPANY_COLUMNS.headcount)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center">
                {formatEmployees(rowData.data.employees) === 'N/A' ? (
                  <EmptyCell />
                ) : (
                  <Text fontSize="sm" color="rgba(255,255,255,0.6)" fontWeight="400">
                    {formatEmployees(rowData.data.employees)}
                  </Text>
                )}
              </Box>

              {/* Revenue Column */}
              <Box {...getColumnStyle(COMPANY_COLUMNS.revenue)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center">
                {formatCurrency(rowData.data.revenue_millions) === 'N/A' ? (
                  <EmptyCell />
                ) : (
                  <Text fontSize="sm" color="rgba(255,255,255,0.6)" fontWeight="400">
                    {formatCurrency(rowData.data.revenue_millions)}
                  </Text>
                )}
              </Box>

              {/* Location Column */}
              <Box {...getColumnStyle(COMPANY_COLUMNS.location)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center">
                {(() => {
                  const loc =
                    rowData.data.city && rowData.data.state
                      ? `${rowData.data.city}, ${rowData.data.state}`
                      : rowData.data.headquarters;
                  if (!loc) return <EmptyCell />;
                  return (
                    <Text fontSize="sm" color="rgba(255,255,255,0.4)" noOfLines={2} fontWeight="400" overflow="hidden" textOverflow="ellipsis">
                      {loc}
                    </Text>
                  );
                })()}
              </Box>

              {/* Custom Field Columns */}
              {companyFieldDefinitions && companyFieldDefinitions.map((fieldDef, idx) => {
                const isPreviewColumn = hasPreviewData && fieldDef.status === 'draft';
                const fieldValue = rowData.data.custom_fields?.[fieldDef.field_id];
                const isCellGenerating = isGeneratingCustomFields && (fieldValue === undefined || fieldValue === null || fieldValue === '');

                return (
                <Box
                  key={`custom_${idx}`}
                  {...getColumnStyle(COMPANY_COLUMNS[`custom_${idx}`])}
                  px={4}
                  bg={isPreviewColumn ? "rgba(49, 130, 206, 0.12)" : "transparent"}
                  borderLeft={isPreviewColumn ? "3px solid" : "none"}
                  borderRight="1px solid"
                  borderColor={isPreviewColumn ? "rgba(49,130,206,0.4)" : "rgba(255,255,255,0.08)"}
                  display="flex"
                  alignItems="flex-start"
                  cursor="pointer"
                  transition="background 0.15s ease"
                  py={2}
                  _hover={{ bg: isPreviewColumn ? "rgba(49, 130, 206, 0.2)" : "rgba(255,255,255,0.04)" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Open modal to view/edit full content
                    setModalFieldName(fieldDef.display_name);
                    setModalFieldValue(fieldValue);
                    setModalFieldDef(fieldDef);
                    // Store edit context for the modal (no-op when campaign is locked)
                    if (!isLocked) {
                      setEditingCell && setEditingCell({ 
                        entityId: entityId, 
                        fieldId: fieldDef.field_id,
                        entityType: 'companies'
                      });
                    }
                    onModalOpen();
                  }}
                >
                  <Box
                    w="100%"
                    maxH="180px"
                    overflow="hidden"
                    title="Click to view/edit full content"
                    css={{
                      display: '-webkit-box',
                      WebkitLineClamp: 8,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    <CustomFieldDisplay value={fieldValue} isGenerating={isCellGenerating} />
                  </Box>
                </Box>
              );
              })}
              
              {/* Links Column */}
              <Box {...getColumnStyle(COMPANY_COLUMNS.links)} px={4} display="flex" alignItems="center" overflow="visible">
                <VStack spacing={1} align="start" w="100%">
                  {rowData.data.website && (
                    <Link
                      href={rowData.data.website.startsWith('http') ?
                        rowData.data.website :
                        `https://${rowData.data.website}`}
                      isExternal
                      color="blue.400"
                      fontSize="xs"
                      fontWeight="500"
                      _hover={{ color: "blue.300" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HStack spacing={1}>
                        <Box as={ExternalLinkIcon} boxSize={3} />
                        <Text>Website</Text>
                      </HStack>
                    </Link>
                  )}
                  {rowData.data.linkedin_url && (
                    <Link
                      href={rowData.data.linkedin_url}
                      isExternal
                      color="#0077B5"
                      fontSize="xs"
                      fontWeight="500"
                      _hover={{ color: "#0099d4" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HStack spacing={1}>
                        <Box as={LinkedInIcon} boxSize={3} />
                        <Text>LinkedIn</Text>
                      </HStack>
                    </Link>
                  )}
                </VStack>
              </Box>
            </HStack>
          </Box>
        </div>
        </>
      );

    case ROW_TYPES.CONTACT:
      const contact = rowData.data;
      const isEnriched = contact.enriched !== false; // Default to enriched if field is missing
      const hasLinkedin = Boolean(contact.has_linkedin || contact.linkedin);
      // For enriched contacts: only show the email row when a real email value
      // is present (hides phony icons for contacts that have no email).
      // For non-enriched (preview mode): the backend strips contact.email and
      // sets has_email: true — use that flag so the blurred placeholder still
      // renders for contacts that do have an email in the DB.
      const hasEmail = Boolean(
        (contact.email && contact.email.trim()) ||
        (!isEnriched && contact.has_email)
      );
      const hasPhone = Boolean(contact.has_phone || (contact.phone_number && String(contact.phone_number).trim()));
      
      // Split phone numbers - handle both ', ' and ',' separators
      const phones = isEnriched && contact.phone_number ? 
        contact.phone_number.split(/,\s*/).filter(p => p.trim()) : [];
      const phoneTypes = isEnriched && contact.phone_type ? 
        contact.phone_type.split(/,\s*/).map(t => t.trim()) : [];
      
      // Create phone entries with 1:1 mapping between phone numbers and types
      const phoneEntries = phones.map((phone, index) => ({
        number: phone,
        type: phoneTypes[index] || null
      }));
      
      return (
        <>
          {/* Notes editing modal */}
          <Modal isOpen={isNotesModalOpen} onClose={onNotesModalClose} size="md" isCentered>
            <ModalOverlay bg="blackAlpha.700" />
            <ModalContent bg="#0a0a0a" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px" color="#fafafa">
              <ModalHeader fontSize="md" fontWeight="600" pb={3} borderBottomWidth="1px" borderColor="rgba(255,255,255,0.08)">Notes</ModalHeader>
              <ModalCloseButton color="#a3a3a3" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} />
              <ModalBody pb={2}>
                <Textarea
                  value={notesEditValue}
                  onChange={(e) => setNotesEditValue(e.target.value)}
                  placeholder="Add notes about this contact..."
                  fontSize="sm"
                  color="#fafafa"
                  bg="#111111"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.12)"
                  _focus={{ borderColor: "rgba(255,255,255,0.35)", boxShadow: "none" }}
                  _hover={{ borderColor: "rgba(255,255,255,0.2)" }}
                  _placeholder={{ color: "rgba(255,255,255,0.25)" }}
                  minH="160px"
                  resize="vertical"
                />
              </ModalBody>
              <ModalFooter gap={2} borderTopWidth="1px" borderColor="rgba(255,255,255,0.08)">
                <Button
                  variant="ghost"
                  size="sm"
                  color="#a3a3a3"
                  borderRadius="full"
                  _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                  _focus={{ boxShadow: 'none' }}
                  onClick={onNotesModalClose}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  bg="#fafafa"
                  color="#0a0a0a"
                  borderRadius="full"
                  fontWeight="600"
                  _hover={{ opacity: 0.85 }}
                  _focus={{ boxShadow: 'none' }}
                  isLoading={notesSaving}
                  onClick={async () => {
                    if (!notesFieldId || !notesMembershipId || !onNotesChange) return;
                    setNotesSaving(true);
                    try {
                      await onNotesChange(notesMembershipId, notesFieldId, notesEditValue);
                      onNotesModalClose();
                    } finally {
                      setNotesSaving(false);
                    }
                  }}
                >
                  Save
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
          <Modal isOpen={isMessageCellModalOpen} onClose={onMessageCellModalClose} size="lg">
            <ModalOverlay bg="blackAlpha.700" />
            <ModalContent bg="#0a0a0a" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px" color="#fafafa">
              <ModalHeader fontSize="md" fontWeight="600" pb={3} borderBottomWidth="1px" borderColor="rgba(255,255,255,0.08)">{messageCellModalColName || 'Message'}</ModalHeader>
              <ModalCloseButton color="#a3a3a3" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} />
              <ModalBody pb={2}>
                {isLocked && (
                  <Box
                    mb={3}
                    px={3}
                    py={2}
                    borderRadius="8px"
                    bg="rgba(234,179,8,0.08)"
                    border="1px solid rgba(234,179,8,0.2)"
                  >
                    <Text fontSize="xs" color="rgba(234,179,8,0.85)">
                      This campaign has been exported and is read-only.
                    </Text>
                  </Box>
                )}
                <Textarea
                  value={messageCellEditValue}
                  onChange={isLocked ? undefined : (e) => setMessageCellEditValue(e.target.value)}
                  isReadOnly={isLocked}
                  fontSize="sm"
                  color="#fafafa"
                  bg="#111111"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.12)"
                  _focus={{ borderColor: isLocked ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.35)", boxShadow: "none" }}
                  _hover={{ borderColor: isLocked ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.2)" }}
                  minH="240px"
                  resize="vertical"
                  whiteSpace="pre-wrap"
                  cursor={isLocked ? 'default' : 'text'}
                />
              </ModalBody>
              <ModalFooter gap={2} borderTopWidth="1px" borderColor="rgba(255,255,255,0.08)">
                <Button
                  variant="ghost"
                  size="sm"
                  color="#a3a3a3"
                  borderRadius="full"
                  _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                  _focus={{ boxShadow: 'none' }}
                  onClick={onMessageCellModalClose}
                >
                  {isLocked ? 'Close' : 'Cancel'}
                </Button>
                {!isLocked && (
                  <Button
                    size="sm"
                    bg="#fafafa"
                    color="#0a0a0a"
                    borderRadius="full"
                    fontWeight="600"
                    _hover={{ opacity: 0.85 }}
                    _focus={{ boxShadow: 'none' }}
                    isLoading={messageCellSaving}
                    isDisabled={messageCellEditValue === messageCellModalValue}
                    onClick={async () => {
                      if (!messageCellFieldId || !messageCellMembershipId || !onMessageDraftCellSave) return;
                      setMessageCellSaving(true);
                      try {
                        await onMessageDraftCellSave(messageCellMembershipId, messageCellFieldId, messageCellEditValue);
                        setMessageCellModalValue(messageCellEditValue);
                        onMessageCellModalClose();
                      } finally {
                        setMessageCellSaving(false);
                      }
                    }}
                  >
                    Save
                  </Button>
                )}
              </ModalFooter>
            </ModalContent>
          </Modal>
          <CustomFieldViewModal
            isOpen={isModalOpen}
            onClose={onModalClose}
            fieldName={modalFieldName}
            fieldValue={modalFieldValue}
            fieldDef={modalFieldDef}
            onSave={isLocked ? undefined : async (newValue) => {
              if (editingCell && handleCellEdit) {
                // Optimistically update the modal display
                setModalFieldValue(newValue);
                // Save to backend
                await handleCellEdit(editingCell.entityId, editingCell.fieldId, newValue, editingCell.entityType);
              }
            }}
          />
          <div style={{...style, height: `${style.height}px`, padding: 0, margin: 0, minWidth: `${CONTACT_TABLE_MIN_WIDTH}px`, width: '100%'}}>
          <Box
            bg={isSelected ? "rgba(255,255,255,0.06)" : "#0a0a0a"}
            borderLeft="1px solid"
            borderRight="1px solid"
            borderBottom="1px solid"
            borderColor="rgba(255,255,255,0.08)"
            mx={0}
            my={0}
            h="100%"
            w="100%"
            _hover={{ bg: isSelected ? "rgba(255,255,255,0.09)" : "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.12)" }}
            position="relative"
            transition="all 0.15s ease"
          >
            <HStack spacing={0} h="100%" align="stretch" w="100%">
              {/* Checkbox Column */}
              <Box {...getColumnStyle(CONTACT_COLUMNS.checkbox)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" justifyContent="center">
                <Checkbox
                  isChecked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onRowSelect && onRowSelect(entityId, index, e.nativeEvent);
                  }}
                  colorScheme="blue"
                />
              </Box>

              {/* Title/Position Column */}
              <Box {...getColumnStyle(CONTACT_COLUMNS.title)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center">
                {contact.position ? (
                  <Text
                    fontSize="sm"
                    color="#fafafa"
                    noOfLines={2}
                    fontWeight="500"
                    lineHeight="1.3"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    cursor={onContactClick ? 'pointer' : 'default'}
                    _hover={onContactClick ? { color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' } : {}}
                    transition="color 0.12s ease"
                    onClick={(e) => {
                      if (!onContactClick) return;
                      e.stopPropagation();
                      onContactClick(contact);
                    }}
                  >
                    {contact.position}
                  </Text>
                ) : (
                  <EmptyCell />
                )}
              </Box>

              {/* Company Column with LinkedIn and Website Icons */}
              <Box {...getColumnStyle(CONTACT_COLUMNS.company)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center">
                {contact.company_name ? (
                  <Text fontSize="sm" color="rgba(255,255,255,0.6)" noOfLines={2} fontWeight="400" lineHeight="1.3" flex="1" minW={0} overflow="hidden" textOverflow="ellipsis">
                    {contact.company_name}
                  </Text>
                ) : (
                  <Box flex="1" minW={0} display="flex" alignItems="center">
                    <EmptyCell />
                  </Box>
                )}
                <HStack spacing={1.5} flexShrink={0} ml={2}>
                  {contact.company_linkedin && (
                    <Link
                      href={contact.company_linkedin}
                      isExternal
                      color="#0077B5"
                      _hover={{ color: "#0099d4" }}
                      onClick={(e) => e.stopPropagation()}
                      display="flex"
                      title="Company LinkedIn"
                    >
                      <Box as={LinkedInIcon} boxSize={3.5} />
                    </Link>
                  )}
                  {contact.company_website && (
                    <Link
                      href={contact.company_website.startsWith('http') ? contact.company_website : `https://${contact.company_website}`}
                      isExternal
                      color="blue.400"
                      _hover={{ color: "blue.300" }}
                      onClick={(e) => e.stopPropagation()}
                      display="flex"
                      title="Company Website"
                    >
                      <Box as={ExternalLinkIcon} boxSize={3.5} />
                    </Link>
                  )}
                </HStack>
              </Box>

              {/* Name Column - click to open contact detail view */}
              <Box {...getColumnStyle(CONTACT_COLUMNS.name)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center">
                <Text
                  fontWeight="400"
                  color="rgba(255,255,255,0.6)"
                  fontSize="sm"
                  noOfLines={2}
                  lineHeight="1.3"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  cursor={onContactClick ? 'pointer' : 'default'}
                  _hover={onContactClick ? { color: 'rgba(255,255,255,0.85)', textDecoration: 'underline' } : {}}
                  transition="color 0.12s ease"
                  onClick={(e) => {
                    if (!onContactClick) return;
                    e.stopPropagation();
                    onContactClick(contact);
                  }}
                >
                  {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown'}
                </Text>
              </Box>

              {/* LinkedIn Column */}
              <Box {...getColumnStyle(CONTACT_COLUMNS.linkedin)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
                {hasLinkedin ? (
                  <HStack spacing={1.5} w="100%" overflow="hidden">
                    <Box as={LinkedInIcon} boxSize={3.5} color="#0077B5" flexShrink={0} />
                    {isEnriched ? (
                      <Link
                        href={contact.linkedin}
                        isExternal
                        color="#0077B5"
                        _hover={{ color: "#0099d4" }}
                        onClick={(e) => e.stopPropagation()}
                        fontSize="xs"
                        noOfLines={1}
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {contact.linkedin.replace(/^https?:\/\/(www\.)?/, '')}
                      </Link>
                    ) : (
                      <Text
                        fontSize="xs"
                        color="rgba(255,255,255,0.3)"
                        filter="blur(4px)"
                        userSelect="none"
                        title="Enrich contact to reveal LinkedIn"
                        noOfLines={1}
                      >
                        {contact.linkedin_preview || 'linkedin.com/in/••••••'}
                      </Text>
                    )}
                  </HStack>
                ) : (
                  <EmptyCell />
                )}
              </Box>

              {/* Email Column */}
              <Box {...getColumnStyle(CONTACT_COLUMNS.email)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
                {hasEmail ? (
                  <HStack spacing={1.5} w="100%" overflow="hidden">
                    <Box as={EmailIcon} boxSize={3.5} color="blue.400" flexShrink={0} />
                    {isEnriched && contact.email ? (
                      <>
                        <Link
                          href={`mailto:${contact.email}`}
                          color="blue.400"
                          _hover={{ color: "blue.300" }}
                          onClick={(e) => e.stopPropagation()}
                          fontSize="xs"
                          noOfLines={1}
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          {contact.email}
                        </Link>
                        {contact.confidence === 'valid' ? (
                          <CheckCircleIcon boxSize={2.5} color="green.400" title="Email verified" flexShrink={0} />
                        ) : (
                          <QuestionIcon boxSize={2.5} color="rgba(255,255,255,0.25)" title="Email not verified" flexShrink={0} />
                        )}
                      </>
                    ) : (
                      <Text
                        fontSize="xs"
                        color="rgba(255,255,255,0.3)"
                        filter="blur(4px)"
                        userSelect="none"
                        title="Enrich contact to reveal email"
                        noOfLines={1}
                      >
                        {contact.email_preview || '••••••@••••.com'}
                      </Text>
                    )}
                  </HStack>
                ) : (
                  <EmptyCell />
                )}
              </Box>

              {/* Phone Column */}
              <Box {...getColumnStyle(CONTACT_COLUMNS.phone)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
                {(isEnriched ? phoneEntries.length > 0 : hasPhone) ? (
                  <VStack spacing={1} align="start" w="100%" maxW="100%">
                    {(isEnriched ? phoneEntries : [{ number: contact.phone_preview || '(•••) •••-••••', type: null }]).map((phoneEntry, idx) => (
                      <HStack key={idx} spacing={1.5} w="100%">
                        <Box as={PhoneIcon} boxSize={3.5} color="blue.400" flexShrink={0} />
                        {isEnriched ? (
                          <Link
                            href={`tel:${phoneEntry.number}`}
                            color="blue.400"
                            _hover={{ color: "blue.300" }}
                            onClick={(e) => e.stopPropagation()}
                            fontSize="xs"
                            noOfLines={1}
                          >
                            {formatPhoneNumber(phoneEntry.number)}
                            {phoneEntry.type && (
                              <Text as="span" color="rgba(255,255,255,0.35)" ml={1}>
                                ({phoneEntry.type})
                              </Text>
                            )}
                          </Link>
                        ) : (
                          <Text
                            fontSize="xs"
                            color="rgba(255,255,255,0.3)"
                            filter="blur(4px)"
                            userSelect="none"
                            title="Enrich contact to reveal phone"
                            noOfLines={1}
                          >
                            {formatPhoneNumber(phoneEntry.number)}
                            {phoneEntry.type && (
                              <Text as="span" color="rgba(255,255,255,0.3)" ml={1}>
                                ({phoneEntry.type})
                              </Text>
                            )}
                          </Text>
                        )}
                      </HStack>
                    ))}
                  </VStack>
                ) : (
                  <EmptyCell />
                )}
              </Box>

              {/* Status Column — contacted checkbox */}
              <Box
                {...getColumnStyle(CONTACT_COLUMNS.status)}
                px={4}
                borderRight="1px solid"
                borderColor="rgba(255,255,255,0.08)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Checkbox
                  isChecked={contactedFieldId ? Boolean(contact.custom_fields?.[contactedFieldId]) : false}
                  colorScheme="green"
                  onChange={(e) => {
                    e.stopPropagation();
                    if (contactedFieldId && onContactedChange) {
                      onContactedChange(contact.membership_id, contactedFieldId, e.target.checked);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Text fontSize="xs" color="rgba(255,255,255,0.4)" userSelect="none">Contacted</Text>
                </Checkbox>
              </Box>

              {/* Notes Column — truncated text, click to edit */}
              <Box
                {...getColumnStyle(CONTACT_COLUMNS.notes)}
                px={4}
                borderRight="1px solid"
                borderColor="rgba(255,255,255,0.08)"
                display="flex"
                alignItems="flex-start"
                py={2}
                cursor="pointer"
                _hover={{ bg: 'rgba(255,255,255,0.04)' }}
                transition="background 0.15s ease"
                onClick={(e) => {
                  e.stopPropagation();
                  setNotesMembershipId(contact.membership_id);
                  setNotesEditValue(notesFieldId ? (contact.custom_fields?.[notesFieldId] || '') : '');
                  onNotesModalOpen();
                }}
              >
                {notesFieldId && contact.custom_fields?.[notesFieldId] ? (
                  <Text
                    fontSize="xs"
                    color="rgba(255,255,255,0.55)"
                    noOfLines={3}
                    lineHeight="1.5"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    w="100%"
                  >
                    {contact.custom_fields[notesFieldId]}
                  </Text>
                ) : (
                  <Text fontSize="xs" color="rgba(255,255,255,0.2)" fontStyle="italic">Add note...</Text>
                )}
              </Box>

              {/* Custom Field Columns for Contacts (exclude tracking fields) */}
              {contactFieldDefinitions && contactFieldDefinitions.filter(f => f.source !== 'tracking').map((fieldDef, idx) => {
                const isPreviewColumn = hasPreviewData && fieldDef.status === 'draft';
                const fieldValue = contact.custom_fields?.[fieldDef.field_id];
                const isCellGenerating = isGeneratingCustomFields && (fieldValue === undefined || fieldValue === null || fieldValue === '');

                return (
                <Box
                  key={`contact_custom_${idx}`}
                  {...getColumnStyle(CONTACT_COLUMNS[`custom_${idx}`])}
                  px={4}
                  bg={isPreviewColumn ? "rgba(49, 130, 206, 0.12)" : "transparent"}
                  borderLeft={isPreviewColumn ? "3px solid" : "none"}
                  borderRight="1px solid"
                  borderColor={isPreviewColumn ? "rgba(49,130,206,0.4)" : "rgba(255,255,255,0.08)"}
                  display="flex"
                  alignItems="flex-start"
                  cursor="pointer"
                  transition="background 0.15s ease"
                  py={2}
                  _hover={{ bg: isPreviewColumn ? "rgba(49, 130, 206, 0.2)" : "rgba(255,255,255,0.04)" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Open modal to view/edit full content
                    setModalFieldName(fieldDef.display_name);
                    setModalFieldValue(fieldValue);
                    setModalFieldDef(fieldDef);
                    // Store edit context for the modal (no-op when campaign is locked)
                    if (!isLocked) {
                      setEditingCell && setEditingCell({ 
                        entityId: entityId, 
                        fieldId: fieldDef.field_id,
                        entityType: 'contacts'
                      });
                    }
                    onModalOpen();
                  }}
                >
                  <Box
                    w="100%"
                    maxH="180px"
                    overflow="hidden"
                    title="Click to view/edit full content"
                    css={{
                      display: '-webkit-box',
                      WebkitLineClamp: 8,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    <CustomFieldDisplay value={fieldValue} isGenerating={isCellGenerating} />
                  </Box>
                </Box>
              );
              })}
              
              {/* Message Draft Columns - Subject and Body with resolved variables */}
              {messageDrafts && messageDrafts.length > 0 && (() => {
                const messageColumns = getMessageColumns(messageDrafts);
                return messageDrafts.map((draft, idx) => {
                  // For new-system (LLM fanout) drafts the per-contact text is saved in
                  // contact_field_values and surfaced via contact.custom_fields. Use that
                  // as the primary source. Fall back to front-end template resolution for
                  // old-system drafts that store templates on the field definition.
                  const savedSubject = draft.subjectFieldId
                    ? (contact.custom_fields?.[draft.subjectFieldId] || null)
                    : null;
                  const savedBody = draft.bodyFieldId
                    ? (contact.custom_fields?.[draft.bodyFieldId] || null)
                    : null;
                  const resolvedSubject = savedSubject ?? resolveTemplateVariables(draft.subjectTemplate, contact);
                  const resolvedBody = savedBody ?? resolveTemplateVariables(draft.bodyTemplate, contact);
                  const isSubjectGenerating = isGeneratingCustomFields && !!draft.subjectFieldId && !savedSubject;
                  const isBodyGenerating = isGeneratingCustomFields && !!draft.bodyFieldId && !savedBody;
                  const isCollapsed = collapsedExportGroupIds.has(draft.id);
                  const showSubject = shouldShowSubjectColumn(draft);
                  
                  // Phase 3.5 compliance decision for this (contact, draft).
                  // Null (= "Not reviewed") is common — we only render a chip
                  // when the advisor actually has reviewers on this campaign
                  // AND a decision has been stamped. Avoids visual noise on
                  // every row for advisors with no compliance flow active.
                  const complianceDecision = advisorComplianceEnabled && getAdvisorComplianceDecision
                    ? getAdvisorComplianceDecision(contact, draft.id)
                    : null;
                  const showComplianceChip = advisorComplianceEnabled && complianceDecision;
                  const isNonCompliantChip = complianceDecision?.status === 'non_compliant';

                  return (
                    <React.Fragment key={`message_${draft.id}`}>
                      {showSubject && (
                      <Box
                        {...getColumnStyle(messageColumns[`message_${idx}_subject`])}
                        px={4}
                        borderRight="1px solid"
                        borderColor="rgba(255,255,255,0.08)"
                        display="flex"
                        flexDirection="column"
                        alignItems="stretch"
                        justifyContent="space-between"
                        transition="background 0.15s ease"
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: "rgba(255,255,255,0.04)" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const val = resolvedSubject || '';
                          setMessageCellModalColName(`${draft.emailName}: Subject`);
                          setMessageCellModalValue(val);
                          setMessageCellEditValue(val);
                          setMessageCellFieldId(draft.subjectFieldId);
                          setMessageCellMembershipId(contact.membership_id);
                          onMessageCellModalOpen();
                        }}
                      >
                        {resolvedSubject
                          ? <Text fontSize="sm" color="gray.300" noOfLines={3} wordBreak="break-word">
                              {resolvedSubject}
                            </Text>
                          : <CustomFieldDisplay value={null} isGenerating={isSubjectGenerating} />
                        }
                        {/*
                          Compliance chip docks to the bottom-right of the
                          subject cell. Keeping the subject text as the
                          first row preserves the primary scannable content;
                          the chip is decorative metadata that belongs out
                          of the reading path. Uses mt="auto" inside the
                          flex-column cell so it sinks to the bottom even
                          on short subject lines.
                        */}
                        {showComplianceChip && (
                          <Box
                            mt={1.5}
                            alignSelf="flex-end"
                            onClick={(e) => {
                              // Non-compliant chips open the reviewer-note diff
                              // modal instead of the cell edit modal — the
                              // advisor's primary need there is "why was this
                              // flagged", not "edit the subject inline". Other
                              // statuses fall through to the cell click.
                              if (isNonCompliantChip && onAdvisorComplianceChipClick) {
                                e.stopPropagation();
                                const firstName = (contact?.first_name || '').trim();
                                const lastName = (contact?.last_name || '').trim();
                                const contactName = [firstName, lastName].filter(Boolean).join(' ')
                                  || contact?.email
                                  || 'Unknown contact';
                                onAdvisorComplianceChipClick({
                                  contact,
                                  draft,
                                  decision: complianceDecision,
                                  currentSubject: resolvedSubject,
                                  currentBody: resolvedBody,
                                  contactName,
                                  draftName: draft?.emailName,
                                });
                              }
                            }}
                            cursor={isNonCompliantChip ? 'pointer' : 'default'}
                          >
                            <ComplianceStatusChip compliance={complianceDecision} compact />
                          </Box>
                        )}
                      </Box>
                      )}
                      {/* Body Column */}
                      <Box
                        {...getColumnStyle(messageColumns[`message_${idx}_body`])}
                        px={4}
                        borderRight="1px solid"
                        borderColor="rgba(255,255,255,0.08)"
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                        transition="background 0.15s ease"
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: "rgba(255,255,255,0.04)" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const val = resolvedBody || '';
                          setMessageCellModalColName(`${draft.emailName}: Body`);
                          setMessageCellModalValue(val);
                          setMessageCellEditValue(val);
                          setMessageCellFieldId(draft.bodyFieldId);
                          setMessageCellMembershipId(contact.membership_id);
                          onMessageCellModalOpen();
                        }}
                      >
                        <Box
                          w="100%"
                          maxH="180px"
                          overflow="hidden"
                          css={{
                            display: '-webkit-box',
                            WebkitLineClamp: 8,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {resolvedBody
                            ? <Text fontSize="sm" color="gray.300" whiteSpace="pre-wrap" wordBreak="break-word">
                                {resolvedBody}
                              </Text>
                            : <CustomFieldDisplay value={null} isGenerating={isBodyGenerating} />
                          }
                        </Box>
                        {showComplianceChip && (
                          <Box
                            mt={1.5}
                            alignSelf="flex-end"
                            onClick={(e) => {
                              if (isNonCompliantChip && onAdvisorComplianceChipClick) {
                                e.stopPropagation();
                                const firstName = (contact?.first_name || '').trim();
                                const lastName = (contact?.last_name || '').trim();
                                const contactName = [firstName, lastName].filter(Boolean).join(' ')
                                  || contact?.email
                                  || 'Unknown contact';
                                onAdvisorComplianceChipClick({
                                  contact,
                                  draft,
                                  decision: complianceDecision,
                                  currentSubject: resolvedSubject,
                                  currentBody: resolvedBody,
                                  contactName,
                                  draftName: draft?.emailName,
                                });
                              }
                            }}
                            cursor={isNonCompliantChip ? 'pointer' : 'default'}
                          >
                            <ComplianceStatusChip compliance={complianceDecision} compact />
                          </Box>
                        )}
                      </Box>
                      {/* Smartlead Export Status Column */}
                      {!isCollapsed && exportedGroupIdsSmartlead.has(draft.id) && (
                        <Box
                          {...getColumnStyle(messageColumns[`message_${idx}_smartlead_exports`])}
                          px={3}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.08)"
                          display="flex"
                          alignItems="flex-start"
                          py={2}
                        >
                          <VStack spacing={1} align="stretch" w="100%">
                            {(() => {
                              const membershipId = contact.membership_id;
                              const badges = exportTracking
                                .filter(exp =>
                                  (exp.platform || 'smartlead') === 'smartlead' &&
                                  exp.message_group_ids?.includes(draft.id) &&
                                  exp.contactSet?.has(membershipId)
                                )
                                .map(exp => exp.external_campaign_name);

                              if (badges.length === 0) {
                                return <EmptyCell />;
                              }
                              return badges.map((name, bi) => (
                                <HStack key={bi} spacing={1}>
                                  <CheckCircleIcon color="green.400" boxSize={3} />
                                  <Text fontSize="xs" color="green.300" noOfLines={1}>
                                    {name}
                                  </Text>
                                </HStack>
                              ));
                            })()}
                          </VStack>
                        </Box>
                      )}
                      {/* HeyReach Export Status Column */}
                      {!isCollapsed && exportedGroupIdsHeyreach.has(draft.id) && (
                        <Box
                          {...getColumnStyle(messageColumns[`message_${idx}_heyreach_exports`])}
                          px={3}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.08)"
                          display="flex"
                          alignItems="flex-start"
                          py={2}
                        >
                          <VStack spacing={1} align="stretch" w="100%">
                            {(() => {
                              const membershipId = contact.membership_id;
                              const badges = exportTracking
                                .filter(exp =>
                                  exp.platform === 'heyreach' &&
                                  exp.message_group_ids?.includes(draft.id) &&
                                  exp.contactSet?.has(membershipId)
                                )
                                .map(exp => exp.external_campaign_name);

                              if (badges.length === 0) {
                                return <EmptyCell />;
                              }
                              return badges.map((name, bi) => (
                                <HStack key={bi} spacing={1}>
                                  <CheckCircleIcon color="green.400" boxSize={3} />
                                  <Text fontSize="xs" color="green.300" noOfLines={1}>
                                    {name}
                                  </Text>
                                </HStack>
                              ));
                            })()}
                          </VStack>
                        </Box>
                      )}
                      {/* GillyReach Export Status Column */}
                      {!isCollapsed && exportedGroupIdsGillyreach.has(draft.id) && (
                        <Box
                          {...getColumnStyle(messageColumns[`message_${idx}_gillyreach_exports`])}
                          px={3}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.08)"
                          display="flex"
                          alignItems="flex-start"
                          py={2}
                        >
                          {(() => {
                            const entries = gillyreachContactStatus?.get(`${contact.membership_id}:${draft.id}`);
                            if (!entries || entries.length === 0) {
                              return <EmptyCell />;
                            }

                            const renderEntry = (entry, idx) => {
                              const Logo = entry.provider === 'outlook' ? OutlookLogo : GmailLogo;
                              const logoPx =
                                entry.provider === 'outlook'
                                  ? GILLYREACH_LOGO_PX.outlook
                                  : GILLYREACH_LOGO_PX.gmail;
                              if (entry.status === 'sent') {
                                return (
                                  <HStack key={idx} spacing={1} align="center">
                                    <Logo size={logoPx} />
                                    {/* <CheckCircleIcon color="green.400" boxSize={3} /> */}
                                    <Text fontSize="xs" color="green.300">Sent</Text>
                                  </HStack>
                                );
                              }
                              if (entry.status === 'failed') {
                                return (
                                  <HStack key={idx} spacing={1} align="center">
                                    <Logo size={logoPx} />
                                    <Text fontSize="xs" color="red.400">Failed</Text>
                                  </HStack>
                                );
                              }
                              if (entry.status === 'cancelled') {
                                return (
                                  <HStack key={idx} spacing={1} align="center">
                                    <Logo size={logoPx} />
                                    <Text fontSize="xs" color="gray.500">Cancelled</Text>
                                  </HStack>
                                );
                              }
                              if (entry.status === 'paused') {
                                return (
                                  <HStack key={idx} spacing={1} align="center">
                                    <Logo size={logoPx} />
                                    <Text fontSize="xs" color="yellow.400">Paused</Text>
                                  </HStack>
                                );
                              }
                              if (entry.status === 'skipped') {
                                return (
                                  <VStack key={idx} spacing={0} align="start">
                                    <HStack spacing={1} align="center">
                                      <Logo size={logoPx} />
                                      <Text fontSize="xs" color="green.300" whiteSpace="nowrap">Replied</Text>
                                    </HStack>
                                    <Text fontSize="xs" color="whiteAlpha.500" whiteSpace="nowrap" pl="1px">follow-up skipped</Text>
                                  </VStack>
                                );
                              }
                              return (
                                <VStack key={idx} spacing={0} align="start">
                                  <HStack spacing={1} align="center">
                                    <Logo size={logoPx} />
                                    <Text fontSize="xs" color="white" whiteSpace="nowrap">Scheduled</Text>
                                  </HStack>
                                  <Text fontSize="xs" color="whiteAlpha.700" whiteSpace="nowrap" pl="1px">{entry.scheduled_at_et}</Text>
                                </VStack>
                              );
                            };

                            if (entries.length === 1) return renderEntry(entries[0], 0);
                            return (
                              <VStack spacing={1.5} align="start">
                                {entries.map((e, i) => renderEntry(e, i))}
                              </VStack>
                            );
                          })()}
                        </Box>
                      )}
                    </React.Fragment>
                  );
                });
              })()}
              
              {/* Location Column */}
              <Box {...getColumnStyle(CONTACT_COLUMNS.location)} px={4} display="flex" alignItems="center">
                {contact.location ? (
                  <Text fontSize="sm" color="rgba(255,255,255,0.4)" noOfLines={2} fontWeight="400" lineHeight="1.3" overflow="hidden" textOverflow="ellipsis">
                    {contact.location}
                  </Text>
                ) : (
                  <EmptyCell />
                )}
              </Box>

              {/* Address Column (Versium postal address, revealed on demand) */}
              {CONTACT_COLUMNS.address && (
              <Box {...getColumnStyle(CONTACT_COLUMNS.address)} px={4} display="flex" alignItems="center">
                {(() => {
                  if (addressFetchingIds.has(contact.id)) {
                    return <Spinner size="xs" color="rgba(255,255,255,0.35)" />;
                  }
                  const addr = contact.address;
                  if (addr && addr.postal_address) {
                    const head = [addr.postal_address, addr.city].filter(Boolean).join(', ');
                    const tail = [addr.state, addr.zip].filter(Boolean).join(' ');
                    const addressLine = [head, tail].filter(Boolean).join(', ');
                    return (
                      <Text fontSize="sm" color="rgba(255,255,255,0.55)" noOfLines={2} fontWeight="400" lineHeight="1.3" overflow="hidden" textOverflow="ellipsis">
                        {addressLine}
                      </Text>
                    );
                  }
                  if (contact.address_fetched) {
                    // Unlocked but Versium had no match for this contact.
                    return <Text fontSize="sm" color="rgba(255,255,255,0.3)">N/A</Text>;
                  }
                  if (contact.has_address) {
                    // Cached match exists globally but not yet unlocked in this campaign.
                    return (
                      <Text fontSize="sm" color="rgba(255,255,255,0.4)" filter="blur(4px)" title="Fetch address to reveal" userSelect="none">
                        ••••• •••••, •• •••••
                      </Text>
                    );
                  }
                  if (!contact.has_phone) {
                    // No phone => Versium is never called, so this is a definitive N/A.
                    return <Text fontSize="sm" color="rgba(255,255,255,0.3)">N/A</Text>;
                  }
                  return <EmptyCell />;
                })()}
              </Box>
              )}
            </HStack>
          </Box>
        </div>
        </>
      );

    case ROW_TYPES.EMPTY_STATE:
      return (
        <div style={style}>
          <Box bg="#0a0a0a" mx={1} my={0.5} h="100%" display="flex" alignItems="center" justifyContent="center" borderRadius="md">
            {rowData.data.isLoading ? (
              <HStack spacing={2}>
                <Spinner size="sm" color="rgba(255,255,255,0.35)" />
                <Text color="rgba(255,255,255,0.3)" fontSize="sm" textAlign="center">
                  {rowData.data.message}
                </Text>
              </HStack>
            ) : (
              <Text color="rgba(255,255,255,0.3)" fontSize="sm" fontStyle="italic" textAlign="center" w="100%">
                {rowData.data.message}
              </Text>
            )}
          </Box>
        </div>
      );

    default:
      return null;
  }
});

export default TableRow;

